import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useToast } from '../components/Toast';

export interface Csat { rating: number; comment?: string | null }
export interface WorklogEntry { id: string; minutes: number; note?: string | null; userId: string; createdAt: string }

export function useCsat(ticketId: string | undefined) {
  const { toast } = useToast();
  const [csat, setCsat] = useState<Csat | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!ticketId) return;
    try { setCsat((await api.get(`/tickets/${ticketId}/csat`)).data.csat); } catch { /* */ } finally { setLoading(false); }
  }, [ticketId]);
  useEffect(() => { load(); }, [load]);

  const submit = async (rating: number, comment?: string) => {
    if (!ticketId) return;
    try { const r = await api.post(`/tickets/${ticketId}/csat`, { rating, comment }); setCsat(r.data.csat); toast('Thanks for your feedback!', 'success'); }
    catch (e: any) { toast(e?.response?.data?.message || 'Could not submit rating', 'error'); }
  };
  return { csat, loading, submit };
}

export function useWorklogs(ticketId: string | undefined, enabled: boolean) {
  const { toast } = useToast();
  const [worklogs, setWorklogs] = useState<WorklogEntry[]>([]);
  const [totalMinutes, setTotal] = useState(0);

  const load = useCallback(async () => {
    if (!ticketId || !enabled) return;
    try { const r = await api.get(`/tickets/${ticketId}/worklogs`); setWorklogs(r.data.worklogs ?? []); setTotal(r.data.totalMinutes ?? 0); }
    catch { /* */ }
  }, [ticketId, enabled]);
  useEffect(() => { load(); }, [load]);

  const add = async (minutes: number, note?: string) => {
    if (!ticketId) return;
    try { const r = await api.post(`/tickets/${ticketId}/worklogs`, { minutes, note }); setWorklogs(r.data.worklogs ?? []); setTotal(r.data.totalMinutes ?? 0); }
    catch (e: any) { toast(e?.response?.data?.message || 'Could not log time', 'error'); }
  };
  return { worklogs, totalMinutes, add };
}

export function useMerge(ticketId: string | undefined) {
  const { toast } = useToast();
  const merge = async (targetId: string): Promise<boolean> => {
    if (!ticketId) return false;
    try { await api.post(`/tickets/${ticketId}/merge`, { targetId }); toast('Ticket merged', 'success'); return true; }
    catch (e: any) { toast(e?.response?.data?.message || 'Merge failed', 'error'); return false; }
  };
  return { merge };
}
